import { useMemo } from 'react'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import type {
  AthleteWithSync,
  ListActivitiesQuery,
  ReportPeriod,
  SportGroup,
  SyncTriggerRequest,
} from '@gd/shared'
import { api, ApiClientError } from './client'
import { keys } from './keys'

export function useMe() {
  return useQuery({
    queryKey: keys.me(),
    queryFn: ({ signal }) => api.get('GET /api/me', { signal }),
  })
}

export function useAthletes() {
  return useQuery({
    queryKey: keys.athletes(),
    queryFn: ({ signal }) => api.get('GET /api/athletes', { signal }),
  })
}

/** Resolves a URL slug to its athlete from the already-fetched athlete list. */
export function useAthlete(slug: string | undefined) {
  const athletesQuery = useAthletes()
  const athlete = useMemo<AthleteWithSync | undefined>(() => {
    if (!slug || !athletesQuery.data) return undefined
    return athletesQuery.data.find((a) => a.slug === slug)
  }, [athletesQuery.data, slug])

  return {
    ...athletesQuery,
    athlete,
    /** True once the athlete list has loaded but no athlete matched the slug. */
    notFound: !athletesQuery.isLoading && !athletesQuery.isError && !!slug && !athlete,
  }
}

export function useSummary(athleteId: string | undefined, date?: string) {
  return useQuery({
    queryKey: keys.summary(athleteId ?? '', date),
    queryFn: ({ signal }) =>
      api.get('GET /api/athletes/:id/summary', {
        params: { id: athleteId! },
        query: { date },
        signal,
      }),
    enabled: !!athleteId,
  })
}

export function useActivities(
  athleteId: string | undefined,
  query: Omit<ListActivitiesQuery, 'cursor'>,
) {
  return useInfiniteQuery({
    queryKey: keys.activities(athleteId ?? '', query),
    queryFn: ({ pageParam, signal }) =>
      api.get('GET /api/athletes/:id/activities', {
        params: { id: athleteId! },
        query: { ...query, cursor: pageParam },
        signal,
      }),
    enabled: !!athleteId,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  })
}

export function useActivity(id: string | undefined) {
  return useQuery({
    queryKey: keys.activity(id ?? ''),
    queryFn: ({ signal }) => api.get('GET /api/activities/:id', { params: { id: id! }, signal }),
    enabled: !!id,
  })
}

export function useWellness(athleteId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: keys.wellness(athleteId ?? '', from, to),
    queryFn: ({ signal }) =>
      api.get('GET /api/athletes/:id/wellness', {
        params: { id: athleteId! },
        query: { from, to },
        signal,
      }),
    enabled: !!athleteId,
  })
}

export function useEvents(athleteId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: keys.events(athleteId ?? '', from, to),
    queryFn: ({ signal }) =>
      api.get('GET /api/athletes/:id/events', {
        params: { id: athleteId! },
        query: { from, to },
        signal,
      }),
    enabled: !!athleteId,
  })
}

export function useWeekly(athleteId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: keys.weekly(athleteId ?? '', from, to),
    queryFn: ({ signal }) =>
      api.get('GET /api/athletes/:id/weekly', {
        params: { id: athleteId! },
        query: { from, to },
        signal,
      }),
    enabled: !!athleteId,
  })
}

export function useFitness(athleteId: string | undefined, from: string, to: string) {
  return useQuery({
    queryKey: keys.fitness(athleteId ?? '', from, to),
    queryFn: ({ signal }) =>
      api.get('GET /api/athletes/:id/fitness', {
        params: { id: athleteId! },
        query: { from, to },
        signal,
      }),
    enabled: !!athleteId,
  })
}

/** Streams (downsampled to `points`), laps, splits and best efforts for one activity. */
export function useActivityDetail(
  id: string | undefined,
  points = 1000,
  splits: 'mi' | 'km' = 'mi',
) {
  return useQuery({
    queryKey: keys.activityDetail(id ?? '', points, splits),
    queryFn: ({ signal }) =>
      api.get('GET /api/activities/:id/detail', {
        params: { id: id! },
        query: { points, splits },
        signal,
      }),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  })
}

/** Training status, HRV status, readiness, recovery, forecast for a date (default today). */
export function useStatus(athleteId: string | undefined, date?: string) {
  return useQuery({
    queryKey: keys.status(athleteId ?? '', date),
    queryFn: ({ signal }) =>
      api.get('GET /api/athletes/:id/status', {
        params: { id: athleteId! },
        query: { date },
        signal,
      }),
    enabled: !!athleteId,
  })
}

export function useRecords(
  athleteId: string | undefined,
  sport: SportGroup,
  from?: string,
  to?: string,
) {
  return useQuery({
    queryKey: keys.records(athleteId ?? '', sport, from, to),
    queryFn: ({ signal }) =>
      api.get('GET /api/athletes/:id/records', {
        params: { id: athleteId! },
        query: { sport, from, to },
        signal,
      }),
    enabled: !!athleteId,
  })
}

export function useReport(athleteId: string | undefined, period: ReportPeriod, date?: string) {
  return useQuery({
    queryKey: keys.report(athleteId ?? '', period, date),
    queryFn: ({ signal }) =>
      api.get('GET /api/athletes/:id/report', {
        params: { id: athleteId! },
        query: { period, date },
        signal,
      }),
    enabled: !!athleteId,
  })
}

export function useBrief(athleteId: string | undefined, days?: number) {
  return useQuery({
    queryKey: keys.brief(athleteId ?? '', days),
    queryFn: ({ signal }) =>
      api.get('GET /api/athletes/:id/brief', {
        params: { id: athleteId! },
        query: { days },
        signal,
      }),
    enabled: !!athleteId,
  })
}

export function useSyncRuns(athleteId?: string, limit?: number) {
  return useQuery({
    queryKey: keys.syncRuns(athleteId, limit),
    queryFn: ({ signal }) => api.get('GET /api/sync/runs', { query: { athleteId, limit }, signal }),
  })
}

export function useTriggerSync() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: SyncTriggerRequest) => api.post('POST /api/sync/trigger', { body }),
    onSuccess: () => {
      toast.success('Synced')
      void queryClient.invalidateQueries({ queryKey: ['sync-runs'] })
      void queryClient.invalidateQueries({ queryKey: ['athletes'] })
      void queryClient.invalidateQueries({ queryKey: ['summary'] })
    },
    onError: (error) => {
      const message = error instanceof ApiClientError ? error.message : 'Sync failed to start'
      toast.error(message)
    },
  })
}
